
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import AirportStatsTable from "@/components/admin/reports/AirportStatsTable";
import SiteStatsGrid from "@/components/admin/reports/SiteStatsGrid";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

interface AirportStats {
  airport_name: string;
  review_count: number;
  average_score: number;
  hand_check_success_rate: number;
}

interface SiteStats {
  total_reviews: number;
  total_airports: number;
  total_users: number;
  average_film_friendly_score: number;
}

export default function AdminReports() {
  const { isLoading } = useAdminAccess();
  const [airportStats, setAirportStats] = useState<AirportStats[]>([]);
  const [siteStats, setSiteStats] = useState<SiteStats | null>(null);

  useEffect(() => {
    if (!isLoading) {
      fetchStats();
    }
  }, [isLoading]);

  const fetchStats = async () => {
    try {
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select(`
          airport_name,
          film_friendly_score,
          hand_check_success
        `);

      if (reviewsError) throw reviewsError;

      // Improved calculation of airport statistics
      const airportStatsMap = new Map<string, {
        review_count: number;
        total_score: number;
        hand_check_successes: number;
      }>();

      reviewsData.forEach((review) => {
        const stats = airportStatsMap.get(review.airport_name) || {
          review_count: 0,
          total_score: 0,
          hand_check_successes: 0,
        };

        stats.review_count++;
        stats.total_score += review.film_friendly_score;
        if (review.hand_check_success) stats.hand_check_successes++;

        airportStatsMap.set(review.airport_name, stats);
      });

      const processedAirportStats: AirportStats[] = Array.from(airportStatsMap.entries())
        .map(([airport_name, stats]) => ({
          airport_name,
          review_count: stats.review_count,
          average_score: Number((stats.total_score / stats.review_count).toFixed(1)),
          hand_check_success_rate: Number(((stats.hand_check_successes / stats.review_count) * 100).toFixed(1)),
        }))
        .sort((a, b) => {
          // Sort by hand check success rate first (descending)
          const rateDiff = b.hand_check_success_rate - a.hand_check_success_rate;
          if (Math.abs(rateDiff) > 0.01) return rateDiff;
          
          // Then by average score (descending)
          const scoreDiff = b.average_score - a.average_score;
          if (Math.abs(scoreDiff) > 0.01) return scoreDiff;
          
          // Finally by number of reviews (descending) as tiebreaker
          return b.review_count - a.review_count;
        });

      setAirportStats(processedAirportStats);

      // Calculate site-wide statistics
      const { data: profiles } = await supabase.from('profiles').select('id');
      const totalUsers = profiles?.length || 0;
      
      const totalReviews = reviewsData.length;
      const totalScore = reviewsData.reduce((acc, review) => acc + review.film_friendly_score, 0);
      
      setSiteStats({
        total_reviews: totalReviews,
        total_airports: airportStatsMap.size,
        total_users: totalUsers,
        average_film_friendly_score: Number((totalReviews > 0 ? totalScore / totalReviews : 0).toFixed(1)),
      });

    } catch (error: any) {
      console.error('Error fetching stats:', error);
      toast.error("Failed to load statistics");
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout title="Site Statistics">
      {siteStats && <SiteStatsGrid stats={siteStats} />}

      <Card>
        <CardHeader>
          <CardTitle>Airport Statistics</CardTitle>
        </CardHeader>
        <CardContent className="px-2 sm:px-6 overflow-auto">
          <AirportStatsTable airportStats={airportStats} />
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
